import * as _ from 'lodash';

/**
 * PRD-1725 - helpers for the admin users importer.
 *
 * The server returns ImportVerification<IamUser> rows whose `mappingErrors` are either plain strings
 * or {type, description, message, fieldName} objects. The exact error taxonomy is not part of the
 * contract, so categorisation is derived here from the message text and kept out of the component -
 * that way it can be re-run on a single row if inline editing is added later.
 */

export type AdminUserImportErrorCategory =
  'duplicateEntry' | 'missingRequiredField' | 'formatMismatch' | 'invalidValue' | 'other';

export interface AdminUserImportMappingError {
  type?: string; // 'Error' | 'Warning'
  code?: string | number;
  description?: string;
  message?: string;
  fieldName?: string;
}

export interface AdminUserImportNormalizedError {
  text: string;
  fieldKey: string;
  category: AdminUserImportErrorCategory;
  isBlocking: boolean;
}

export interface AdminUserImportCategoryTotals {
  missingRequiredField: number;
  formatMismatch: number;
  duplicateEntry: number;
  invalidValue: number;
  other: number;
}

export interface AdminUserImportDisplay {
  email: string;
  firstName: string;
  lastName: string;
  mobilePhone: string;
  store: string;
  department: string;
  role: string;
  locale: string;
  timezone: string;
  sisenseDashboardId: string;
  groupIds: string;
}

export interface AdminUserImportRow {
  rowNumber?: number;
  entity?: any;
  rawRow?: any;
  mappingErrors?: Array<AdminUserImportMappingError | string>;

  // Client only, precomputed once per row so the template holds no function bindings.
  rowId?: string;
  errors?: AdminUserImportNormalizedError[];
  cellErrors?: AdminUserImportDisplay;
  categories?: AdminUserImportErrorCategory[];
  hasErrors?: boolean;
  hasWarnings?: boolean;
  isDuplicate?: boolean;
  display?: AdminUserImportDisplay;
  searchText?: string;
}

/**
 * Ordered - duplicates must be tested first or "already exists" is swallowed by the invalid-value
 * rule. Retune this table once the backend error strings are known.
 */
export const ERROR_CATEGORY_MATCHERS: Array<{category: AdminUserImportErrorCategory, test: RegExp}> = [
  {category: 'duplicateEntry', test: /duplicat|already\s*exist|already\s*in\s*use|already\s*taken|conflict/i},
  {category: 'missingRequiredField', test: /required|mandatory|missing|is\s*empty|is\s*blank|not\s*provided|cannot\s*be\s*null/i},
  {category: 'formatMismatch', test: /format|pattern|malformed|unparse|parse|not\s*a\s*valid\s*(date|number|email|phone)/i},
  {category: 'invalidValue', test: /invalid|illegal|unknown|unsupported|not\s*found|does\s*not\s*exist|out\s*of\s*range/i}
];

/**
 * Column -> the normalized field names the server may report an error against. Lets the template
 * look up cell errors with fixed keys regardless of the backend's casing/naming.
 */
const FIELD_KEY_ALIASES: {[column: string]: string[]} = {
  email: ['email', 'emailaddress', 'username'],
  firstName: ['firstname', 'first'],
  lastName: ['lastname', 'last', 'surname'],
  mobilePhone: ['mobilephone', 'mobile', 'phone', 'phonenumber'],
  store: ['store'],
  department: ['department', 'dept'],
  role: ['role', 'roles', 'roleid', 'roleids', 'rolename'],
  locale: ['locale', 'localecode', 'language'],
  timezone: ['timezone', 'zoneid', 'timezoneid'],
  sisenseDashboardId: ['sisensedashboardid', 'sisensedefaultdashboardid', 'dashboardid'],
  groupIds: ['groupids', 'groupid', 'group', 'groups', 'rolesgroupid', 'rolesgroupids']
};

const EMPTY_DISPLAY: AdminUserImportDisplay = {
  email: '', firstName: '', lastName: '', mobilePhone: '', store: '', department: '',
  role: '', locale: '', timezone: '', sisenseDashboardId: '', groupIds: ''
};

export function getErrorText(error: AdminUserImportMappingError | string): string {
  if (_.isString(error)) {
    return error;
  }
  return _.get(error, 'description') || _.get(error, 'message') || _.get(error, 'fieldName') || '';
}

/**
 * Same rule the agent assignments importer uses - anything that is not explicitly a Warning blocks
 * the row from being imported.
 */
export function isBlockingError(error: AdminUserImportMappingError | string): boolean {
  return _.isString(error) || !error.type || error.type === 'Error';
}

export function normalizeFieldKey(fieldName: any): string {
  return String(fieldName || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

export function categorizeError(error: AdminUserImportMappingError | string): AdminUserImportErrorCategory {
  const haystack = _.isString(error)
    ? error
    : [getErrorText(error), error.fieldName, error.code].join(' ');

  const matcher = _.find(ERROR_CATEGORY_MATCHERS, (m) => m.test.test(haystack));
  return matcher ? matcher.category : 'other';
}

export function normalizeErrors(mappingErrors: Array<AdminUserImportMappingError | string>): AdminUserImportNormalizedError[] {
  return _.chain(mappingErrors || [])
    .map((error) => {
      const text = getErrorText(error);
      if (!text) {
        return undefined;
      }
      return {
        text: text,
        fieldKey: _.isString(error) ? '' : normalizeFieldKey(error.fieldName),
        category: categorizeError(error),
        isBlocking: isBlockingError(error)
      };
    })
    .compact()
    .uniqBy('text')
    .value();
}

/**
 * Duplicates are detected from the message only. Keying off a populated `entity.id` would flag every
 * row on a backend that echoes ids back.
 */
export function isDuplicateRow(errors: AdminUserImportNormalizedError[]): boolean {
  return _.some(errors, (error) => error.category === 'duplicateEntry');
}

/**
 * Per-column error messages, so a cell can be highlighted and carry its own tooltip.
 */
export function buildCellErrors(errors: AdminUserImportNormalizedError[]): AdminUserImportDisplay {
  const cellErrors: any = _.clone(EMPTY_DISPLAY);

  _.forEach(FIELD_KEY_ALIASES, (aliases, column) => {
    const messages = _.chain(errors)
      .filter((error) => !!error.fieldKey && _.includes(aliases, error.fieldKey))
      .map('text')
      .value();

    cellErrors[column] = messages.length ? messages.join(', ') : '';
  });

  return cellErrors as AdminUserImportDisplay;
}

/**
 * Display strings for every column. The import DTO field names are not fully pinned down, hence the
 * fallbacks - an unmapped value shows as '-' rather than blank.
 */
export function buildDisplay(entity: any): AdminUserImportDisplay {
  const groupIds = _.get(entity, 'groupIds') || _.get(entity, 'rolesGroupIds');
  const role = _.get(entity, 'role') || _.get(entity, 'roles') || _.get(entity, 'roleNames');

  return {
    email: toDisplayValue(_.get(entity, 'email')),
    firstName: toDisplayValue(_.get(entity, 'firstName')),
    lastName: toDisplayValue(_.get(entity, 'lastName')),
    mobilePhone: toDisplayValue(_.get(entity, 'mobilePhone') || _.get(entity, 'phone')),
    store: toDisplayValue(_.get(entity, 'store')),
    department: toDisplayValue(_.get(entity, 'department')),
    role: toDisplayValue(role),
    locale: toDisplayValue(_.get(entity, 'localeCode') || _.get(entity, 'locale')),
    timezone: toDisplayValue(_.get(entity, 'zoneId') || _.get(entity, 'timezone')),
    sisenseDashboardId: toDisplayValue(_.get(entity, 'sisenseDefaultDashboardId') || _.get(entity, 'sisenseDashboardId')),
    groupIds: toDisplayValue(groupIds)
  };
}

export function countRowsByCategory(rows: AdminUserImportRow[]): AdminUserImportCategoryTotals {
  const totals: AdminUserImportCategoryTotals = {
    missingRequiredField: 0,
    formatMismatch: 0,
    duplicateEntry: 0,
    invalidValue: 0,
    other: 0
  };

  // A row with three missing fields counts once per category, so the counters read as "rows with".
  _.forEach(rows, (row) => {
    _.forEach(row.categories || [], (category) => {
      totals[category] = totals[category] + 1;
    });
  });

  return totals;
}

// ******************************
//        Private helpers
// ******************************

function toDisplayValue(value: any): string {
  if (_.isArray(value)) {
    return value.length ? value.join(', ') : '-';
  }
  if (value === undefined || value === null || value === '') {
    return '-';
  }
  return String(value);
}
